import type { ComponentProps } from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { ButtonLoader } from "@/components/ui/loader";
import { cn } from "@/lib/utils";

// One fill colour across the whole ladder; emphasis is carried by how much of
// it a variant shows. `destructive` is the only hue break, so red reads as
// "this is irreversible" rather than as decoration.
const buttonVariants = cva(
  [
    "inline-flex items-center justify-center gap-2 whitespace-nowrap",
    "rounded-md font-medium",
    // A press pulls the control in a touch, so the click reads back before
    // anything it started has answered. Transform is part of the transition
    // so the release is a settle rather than a snap.
    "transition-[color,background-color,border-color,transform] duration-150 ease-out",
    "active:scale-[0.97]",
    "outline-none focus-visible:ring-2 focus-visible:ring-ring",
    "focus-visible:ring-offset-2 focus-visible:ring-offset-background",
    // No pointer-events-none: a disabled control still explains itself
    // through its title, and a disabled button fires no click anyway.
    "disabled:opacity-50",
    "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg]:size-4",
  ].join(" "),
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground hover:bg-primary-hover active:bg-primary-active",
        secondary:
          "border border-border bg-secondary text-secondary-foreground hover:bg-neutral active:bg-neutral",
        outline:
          "border border-border bg-transparent text-foreground hover:bg-neutral active:bg-neutral",
        ghost:
          "bg-transparent text-foreground hover:bg-neutral hover:text-primary active:bg-neutral active:text-primary",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive-hover active:bg-destructive-active",
        link: "bg-transparent text-link underline-offset-4 hover:text-link-hover hover:underline active:text-link-hover",
      },
      size: {
        sm: "h-8 px-3 text-xs",
        default: "h-9 px-4 text-sm",
        lg: "h-10 px-5 text-base",
        icon: "size-9 px-0",
        // For controls that sit inside running text or a dense table head,
        // where a chip would break the row. Type is inherited rather than set,
        // and the variant's hover box is dropped — a tight background
        // highlight on a zero-padding control reads as a glitch. Only the
        // background is neutralised, so each variant keeps its own hover
        // colour; declared after `variant` so tailwind-merge lets it win.
        inline:
          "h-auto w-fit gap-1.5 rounded-xs px-0 py-1 font-[inherit] text-[length:inherit] hover:bg-transparent active:bg-transparent active:scale-100",
      },
    },
    defaultVariants: { variant: "default", size: "default" },
  },
);

type ButtonProps = ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    loading?: boolean;
    // Only for icon-only buttons. When the button has a visible label, leave
    // this unset: the label stays put and aria-busy carries the state, so the
    // accessible name does not change out from under a caller or a test.
    loadingLabel?: string;
  };

export function Button({
  className,
  variant,
  size,
  asChild = false,
  loading = false,
  loadingLabel,
  disabled,
  type,
  children,
  ...props
}: ButtonProps) {
  // Slot forwards onto exactly one child, so an asChild button renders its
  // child untouched — a link styled as a button has nothing to load anyway.
  if (asChild) {
    return (
      <Slot
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
      >
        {children}
      </Slot>
    );
  }
  return (
    <button
      className={cn(buttonVariants({ variant, size }), className)}
      // A button inside a form defaults to submit, which silently posts the
      // form when the author meant an ordinary click handler.
      type={type ?? "button"}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? <ButtonLoader label={loadingLabel} /> : null}
      {children}
    </button>
  );
}

export { buttonVariants };
export type { ButtonProps };
